
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import Index from "./pages/Index";
import DecisionTree from "./pages/DecisionTree";
import Confirmation from "./pages/Confirmation";
import MultiProductQuote from "./pages/MultiProductQuote";
import ProductDetail from "./pages/ProductDetail";
import QuoteForm from "./pages/QuoteForm";
import QuoteSubmitted from "./pages/QuoteSubmitted";
import QuotePrice from "./pages/QuotePrice";
import AboutUs from "./pages/AboutUs";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import ContactUs from "./pages/ContactUs";
import BusinessInsuranceGuide from "./pages/BusinessInsuranceGuide";
import NotFound from "./pages/NotFound";
import SMEPackageQuote from "./pages/SMEPackageQuote";

// Product pages
import PropertyInsurance from "./pages/products/PropertyInsurance";
import LiabilityCoverage from "./pages/products/LiabilityCoverage";
import EmployeeBenefits from "./pages/products/EmployeeBenefits";
import CyberProtection from "./pages/products/CyberProtection";
import FleetInsurance from "./pages/products/FleetInsurance";
import ProfessionalIndemnity from "./pages/products/ProfessionalIndemnity";
import BusinessInterruption from "./pages/products/BusinessInterruption";
import WorkersCompensation from "./pages/products/WorkersCompensation";
import ProductLiability from "./pages/products/ProductLiability";
import BlogIndexPage from "./pages/blog/Index";
import BlogPostPage from "./pages/blog/Post";

// Admin imports
import { AuthProvider } from "@/components/auth/AuthProvider";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ScrollToTop } from "@/components/ScrollToTop";
import Login from "./pages/admin/Login";
import Dashboard from "./pages/admin/Dashboard";
import Metrics from "./pages/admin/Metrics";
import DatabaseHealth from "./pages/admin/DatabaseHealth";
import ExcelExports from "./pages/admin/ExcelExports";
import FileManagement from "./pages/admin/FileManagement";
import SubmissionTracker from "./pages/admin/SubmissionTracker";
import TenantAuditsSubmissions from "./pages/admin/TenantAuditsSubmissions";
import TenantDCCSubmissions from "./pages/admin/TenantDCCSubmissions";
import TenantIncorpifySubmissions from "./pages/admin/TenantIncorpifySubmissions";
import TenantVirtuzoneSubmissions from "./pages/admin/TenantVirtuzoneSubmissions";
import BlogListPage from "./pages/admin/blog/List";
import BlogEditPage from "./pages/admin/blog/Edit";
import BlogSectionPage from "./pages/blog/Section";
import TenantIncorpifyAdmin from "./pages/admin/TenantIncorpifyAdmin";
import TenantAuditsAdmin from "./pages/admin/TenantAuditsAdmin";
import TenantDCCAdmin from "./pages/admin/TenantDCCAdmin";
import TenantVirtuzoneAdmin from "./pages/admin/TenantVirtuzoneAdmin";
import AllLeads from "./pages/admin/AllLeads";

// Tenant imports
import { TenantProvider, useTenant } from "@/contexts/TenantContext";
import TenantDashboard from "./pages/TenantDashboard";
import TenantIndex from "./pages/TenantIndex";
import { TenantManagement } from "@/components/admin/TenantManagement";
import { SupabaseConnectionTest } from "@/components/SupabaseConnectionTest";

// SubdomainRouter logic moved inline to avoid import issues

import {
  getBaseUrl,
  getRootDomains,
  getTenantDomains,
  getCurrentHost,
  isRootDomain,
  isTenantDomain,
  isDomainValid,
  getSubdomain,
  linkTo,
} from '@/lib/utils'

const queryClient = new QueryClient();

// Simple subdomain router component
const SubdomainRouter = () => {
  const { tenant, loading } = useTenant();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin mx-auto mb-4 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Prefer actual tenant presence over env allowlist to avoid manual domain whitelisting for each new tenant
  if (tenant) {
    return <TenantIndex />;
  }

  // If a subdomain exists (including subdomain.localhost), render tenant index and let it fetch branding itself
  const sub = getSubdomain();
  if (sub && !['www','admin','api'].includes(sub)) {
    return <TenantIndex />;
  }

  // Fallback: if host is explicitly marked as tenant domain via env, still render tenant index
  if (isTenantDomain()) {
    return <TenantIndex />;
  }

  // Default: show main site
  return <Index />;
};

console.log('BUILD_MARKER', 'dcc-hard-local-public');
console.log( "getRootDomains():", getRootDomains() );
console.log( "getTenantDomains():", getTenantDomains() );
console.log( "getCurrentHost():", getCurrentHost() );
console.log( "isRootDomain():", isRootDomain() );
console.log( "isTenantDomain():", isTenantDomain() );
console.log( "isDomainValid('localhost'):", isDomainValid('localhost') );
console.log( "isDomainValid('audits.localhost'):", isDomainValid('audits.localhost') );
console.log( "isDomainValid('salman.yc.local'):", isDomainValid('salman.yc.local') );
console.log( "getSubdomain():", getSubdomain() );
console.log( "linkTo(path: string):", linkTo("/my-path-based-on-subdomain") );
console.log( "getBaseUrl():", getBaseUrl() );


// Redirect component: when using path-based preview (/site/:sub/*), send users to the proper subdomain
const TenantPathRedirect = () => {
  const { sub } = useParams();
  useEffect(() => {
    if (!sub) return;
    const { protocol, hostname, port, pathname, search, hash } = window.location;
    // Compute base host suffix (keep last two labels, or localhost)
    const parts = hostname.split('.');
    const isLocalhost = hostname === 'localhost' || hostname.endsWith('.localhost');
    const suffix = isLocalhost
      ? 'localhost'
      : (parts.length >= 2 ? parts.slice(-2).join('.') : hostname);
    const hostWithSub = `${sub}.${suffix}${port ? `:${port}` : ''}`;

    // Preserve the path after /site/:sub
    const match = pathname.match(/^(?:\/business)?\/site\/[^/]+(.*)$/);
    const restPath = (match && match[1]) || '/';

    const target = `${protocol}//${hostWithSub}${restPath}${search || ''}${hash || ''}`;
    // Use replace so history doesn't keep the placeholder /site route
    window.location.replace(target);
  }, [sub]);
  return null;
};


const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter basename={getBaseUrl()}>
        <TenantProvider>
          <AuthProvider>
            <ScrollToTop />
            <Routes>
            {/* Main route - handles both main site and subdomain routing */}
            <Route path="/" element={<SubdomainRouter />} />

            <Route path="/decision-tree" element={<DecisionTree />} />
            <Route path="/confirmation" element={<Confirmation />} />
            <Route path="/multi-quote" element={<MultiProductQuote />} />
            <Route path="/products/:productId" element={<ProductDetail />} />
            <Route path="/quote/sme-package" element={<SMEPackageQuote />} />
            <Route path="/quote/:productId" element={<QuoteForm />} />
            <Route path="/quote-submitted" element={<QuoteSubmitted />} />
            <Route path="/quote-price" element={<QuotePrice />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/business-insurance-guide" element={<BusinessInsuranceGuide />} />

            {/* Product pages */}
            <Route path="/products/property-insurance" element={<PropertyInsurance />} />
            <Route path="/products/liability-coverage" element={<LiabilityCoverage />} />
            <Route path="/products/employee-benefits" element={<EmployeeBenefits />} />
            <Route path="/products/cyber-protection" element={<CyberProtection />} />
            <Route path="/products/fleet-insurance" element={<FleetInsurance />} />
            <Route path="/products/professional-indemnity" element={<ProfessionalIndemnity />} />
            <Route path="/products/business-interruption" element={<BusinessInterruption />} />
            {/* Blog routes */}
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/products/workers-compensation" element={<WorkersCompensation />} />
            {/* Chronicle routes (alias of Blog) */}
            <Route path="/chronicle" element={<BlogIndexPage />} />
            <Route path="/chronicle/section/:sectionSlug" element={<BlogSectionPage />} />
            <Route path="/chronicle/:slug" element={<BlogPostPage />} />

            <Route path="/products/product-liability" element={<ProductLiability />} />


            {/* Path-based tenant preview (for local testing and QA) now redirects to subdomain. */}
            <Route path="/site/:sub/*" element={<TenantPathRedirect />} />

            {/* Admin routes */}
            <Route path="/system/access" element={<Login />} />
            <Route path="/system" element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="metrics" element={<Metrics />} />
              <Route path="database" element={<DatabaseHealth />} />
              <Route path="exports" element={<ExcelExports />} />
              <Route path="files" element={<FileManagement />} />
              <Route path="submissions" element={<SubmissionTracker />} />
              <Route path="leads" element={<AllLeads />} />
              <Route path="tenant-audits" element={<TenantAuditsSubmissions />} />
              <Route path="tenant-dcc" element={<TenantDCCSubmissions />} />
              <Route path="tenant-incorpify" element={<TenantIncorpifySubmissions />} />
              <Route path="tenant-virtuzone" element={<TenantVirtuzoneSubmissions />} />
              <Route path="tenants" element={<TenantManagement />} />
              {/* Blog CMS */}
              <Route path="blog" element={<BlogListPage />} />
              <Route path="blog/new" element={<BlogEditPage />} />
              <Route path="blog/:id" element={<BlogEditPage />} />
            </Route>

            {/* Tenant-specific minimal admin for Audits */}
            <Route path="/audits/admin/access" element={<Login />} />
            <Route path="/audits/admin" element={
              <ProtectedRoute>
                <TenantAuditsAdmin />
              </ProtectedRoute>
            } />

            {/* Tenant-specific minimal admin for Dubai CommerCity */}
            {/* Note: DCC uses inline login on its dashboard page; no separate /access route. */}
            <Route path="/dubaicommercity/admin" element={
              <ProtectedRoute>
                <TenantDCCAdmin />
              </ProtectedRoute>
            } />

            {/* Tenant-specific minimal admin for Incorpify */}
            <Route path="/incorpify/admin/access" element={<Login />} />
            <Route path="/incorpify/admin" element={
              <ProtectedRoute>
                <TenantIncorpifyAdmin />
              </ProtectedRoute>
            } />

            {/* Tenant-specific minimal admin for Virtuzone */}
            <Route path="/virtuzone/admin" element={
              <ProtectedRoute>
                <TenantVirtuzoneAdmin />
              </ProtectedRoute>
            } />

            {/* Tenant-specific routes */}
            <Route path="/tenant-dashboard" element={<TenantDashboard />} />
            {/* Protected tenant dashboard under /system */}
            <Route path="/system/tenant/:subdomain" element={
              <ProtectedRoute>
                <TenantDashboard />
              </ProtectedRoute>
            } />

            {/* Legacy path-based routing removed in favor of subdomains; keep the tenant redirect only */}

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="/debug/supabase" element={<SupabaseConnectionTest />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </TenantProvider>
    </BrowserRouter>
  </TooltipProvider>
</QueryClientProvider>
);

export default App;
